import { useEffect } from "react";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { isAndroidApp, isNativeApp } from "@/platform";

function getScrollableAncestor(el: HTMLElement): HTMLElement {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.body;
}

export function useNativeKeyboardInsets() {
  useEffect(() => {
    if (!isNativeApp()) return;

    Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(err => {
      console.warn("Failed to set keyboard resize mode:", err);
    });

    let paddedElement: HTMLElement | null = null;

    const showListener = Keyboard.addListener("keyboardWillShow", info => {
      const focused = document.activeElement as HTMLElement | null;
      const inDrawer = focused?.closest("[data-vaul-drawer]");

      if (inDrawer && isAndroidApp()) {
        document.documentElement.style.setProperty("--keyboard-height", "0px");
      } else if (inDrawer && focused?.closest("[data-no-keyboard-reposition]")) {
        document.documentElement.style.setProperty("--keyboard-height", "0px");
      } else {
        document.documentElement.style.setProperty("--keyboard-height", `${info.keyboardHeight}px`);
      }

      if (focused && (focused.tagName === "INPUT" || focused.tagName === "TEXTAREA")) {
        if (inDrawer) {
          if (paddedElement) {
            paddedElement.style.paddingBottom = "0px";
            paddedElement = null;
          }
          return;
        }

        const scrollable = getScrollableAncestor(focused);
        if (paddedElement && paddedElement !== scrollable) {
          paddedElement.style.paddingBottom = "0px";
        }
        scrollable.style.paddingBottom = `${info.keyboardHeight}px`;
        paddedElement = scrollable;

        window.setTimeout(() => {
          focused.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    });

    const hideListener = Keyboard.addListener("keyboardWillHide", () => {
      document.documentElement.style.setProperty("--keyboard-height", "0px");
      if (paddedElement) {
        paddedElement.style.paddingBottom = "0px";
        paddedElement = null;
      }
    });

    return () => {
      void showListener.then(listener => listener.remove());
      void hideListener.then(listener => listener.remove());
    };
  }, []);
}
